import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormHelperText
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import apiClient from '../../../config/axios';

dayjs.locale('it');

const getInitialFormData = () => ({
  name: '',
  amount: '',
  referenceMonth: dayjs(),
  note: '',
  expenseType: 'generale',
  selectedCourses: []
});

const getCourseLabel = (course) => {
  const venue = course?.venue?.trim();
  return `${course.name} (${venue || 'Sede non specificata'})`;
};

const ModalExpense = ({ open, onClose, onSuccess, expense = null }) => {
  const isEditMode = !!expense;
  const [formData, setFormData] = useState(getInitialFormData());
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    if (open) {
      fetchCourses();
    }
  }, [open]);

  useEffect(() => {
    if (open && expense) {
      setFormData({
        name: expense.name || '',
        amount: Math.abs(expense.amount) || '',
        referenceMonth: expense.referenceMonth ? dayjs(expense.referenceMonth) : dayjs(),
        note: expense.note || '',
        expenseType: expense.courses && expense.courses.length > 0 ? 'per_corso' : 'generale',
        selectedCourses: expense.courses || []
      });
    } else if (open && !expense) {
      setFormData(getInitialFormData());
    }
  }, [open, expense]);

  const fetchCourses = async () => {
    try {
      const response = await apiClient.get('/api/courses?status=published');
      setCourses(response.data || []);
    } catch (err) {
      console.error('Error fetching courses:', err);
      setCourses([]);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome è obbligatorio';
    }

    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'Importo deve essere maggiore di 0';
    }

    if (!formData.referenceMonth) {
      newErrors.referenceMonth = 'Mese di riferimento è obbligatorio';
    }

    if (formData.expenseType === 'per_corso' && formData.selectedCourses.length === 0) {
      newErrors.selectedCourses = 'Seleziona almeno un corso';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        amount: parseFloat(formData.amount),
        referenceMonth: formData.referenceMonth.toISOString(),
        note: formData.note.trim() || undefined
      };

      if (formData.expenseType === 'per_corso') {
        payload.courses = formData.selectedCourses.map(c => c._id);
      }

      let response;
      if (isEditMode) {
        response = await apiClient.put(`/api/finance/expenses/${expense.id}`, payload);
      } else {
        response = await apiClient.post('/api/finance/expenses', payload);
      }

      setFormData(getInitialFormData());
      setErrors({});

      onSuccess(response.data);
      onClose();
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} expense:`, error);
      const errorMessage = error.response?.data?.error || `Errore durante ${isEditMode ? 'la modifica' : 'la creazione'} della spesa`;
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setFormData(getInitialFormData());
    setErrors({});
    onClose();
  };

  const splitAmount = formData.expenseType === 'per_corso' && formData.selectedCourses.length > 0 && formData.amount > 0
    ? (parseFloat(formData.amount) / formData.selectedCourses.length).toFixed(2)
    : null;

  const allCoursesSelected =
    courses.length > 0 &&
    courses.every((c) => formData.selectedCourses.some((s) => s._id === c._id));

  const handleToggleSelectAllCourses = () => {
    if (allCoursesSelected) {
      handleInputChange('selectedCourses', []);
    } else {
      handleInputChange('selectedCourses', [...courses]);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
      <Dialog
        open={open}
        onClose={(e, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditMode ? 'Modifica Spesa' : 'Aggiungi Spesa'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Tipo di spesa</Typography>
              <ToggleButtonGroup
                value={formData.expenseType}
                exclusive
                onChange={(_, value) => {
                  if (value !== null) {
                    handleInputChange('expenseType', value);
                    if (value === 'generale') {
                      handleInputChange('selectedCourses', []);
                    }
                  }
                }}
                size="small"
                fullWidth
                disabled={loading}
                sx={{
                  '& .MuiToggleButton-root.Mui-selected': {
                    bgcolor: 'common.black',
                    color: 'common.white',
                    '&:hover': {
                      bgcolor: 'common.black',
                      color: 'common.white',
                    },
                  },
                  '& .MuiToggleButton-root.Mui-disabled.Mui-selected': {
                    bgcolor: 'common.black',
                    color: 'common.white',
                    opacity: 1,
                  },
                }}
              >
                <ToggleButton value="generale">Generale</ToggleButton>
                <ToggleButton value="per_corso">Per Corso</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1, lineHeight: 1.5 }}>
                <strong>Generale:</strong> questa spesa non verrà imputata a nessun corso.
                <br />
                <strong>Per Corso:</strong> questa spesa verrà ripartita tra i corsi selezionati.
              </Typography>
            </Box>

            {formData.expenseType === 'per_corso' && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -0.5 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleToggleSelectAllCourses}
                    disabled={loading || courses.length === 0}
                  >
                    {allCoursesSelected ? 'Deseleziona tutti' : 'Seleziona tutti'}
                  </Button>
                </Box>
                <FormControl fullWidth error={!!errors.selectedCourses} required disabled={loading}>
                  <InputLabel>Corsi</InputLabel>
                  <Select
                    multiple
                    value={formData.selectedCourses.map(c => c._id)}
                    onChange={(e) => {
                      const ids = e.target.value;
                      const selected = courses.filter(c => ids.includes(c._id));
                      handleInputChange('selectedCourses', selected);
                    }}
                    label="Corsi"
                    renderValue={(selectedIds) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selectedIds.map(id => {
                          const course = courses.find(c => c._id === id);
                          return course ? (
                            <Chip key={id} label={getCourseLabel(course)} size="small" />
                          ) : null;
                        })}
                      </Box>
                    )}
                    MenuProps={{
                      PaperProps: {
                        style: { maxHeight: 300 }
                      }
                    }}
                  >
                    {courses.map((course) => (
                      <MenuItem key={course._id} value={course._id}>
                        <Checkbox checked={formData.selectedCourses.some(c => c._id === course._id)} />
                        <ListItemText primary={getCourseLabel(course)} />
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.selectedCourses && (
                    <FormHelperText>{errors.selectedCourses}</FormHelperText>
                  )}
                </FormControl>
              </>
            )}

            <TextField
              label="Nome Spesa"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              required
              fullWidth
              disabled={loading}
            />

            <TextField
              label="Importo (€)"
              type="number"
              value={formData.amount}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              error={!!errors.amount}
              helperText={errors.amount}
              required
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
              disabled={loading}
            />

            {splitAmount && (
              <Typography variant="body2" color="text.secondary">
                L'importo verrà ripartito in {formData.selectedCourses.length} corsi: {splitAmount}€ per corso
              </Typography>
            )}

            <DatePicker
              label="Mese di Riferimento"
              value={formData.referenceMonth}
              onChange={(value) => handleInputChange('referenceMonth', value)}
              views={['year', 'month']}
              openTo="month"
              slotProps={{
                textField: {
                  error: !!errors.referenceMonth,
                  helperText: errors.referenceMonth,
                  required: true,
                  fullWidth: true,
                  disabled: loading
                }
              }}
            />

            <TextField
              label="Note (opzionale)"
              value={formData.note}
              onChange={(e) => handleInputChange('note', e.target.value)}
              multiline
              rows={3}
              fullWidth
              disabled={loading}
              inputProps={{ maxLength: 500 }}
              helperText={`${formData.note.length}/500 caratteri`}
            />

            {errors.submit && (
              <Typography color="error" variant="body2">
                {errors.submit}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={loading}
            color="primary"
          >
            Annulla
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? 'Salvataggio...' : (isEditMode ? 'Aggiorna' : 'Salva')}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ModalExpense;
